import React, { useState, useEffect } from 'react'
import Container from '@material-ui/core/Container'
import Disclaimer from 'components/disclaimer'
import PGAIcon from 'images/pga-seal-icon.svg'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme, makeStyles } from '@material-ui/core/styles'
import { useAuth } from 'lib/auth'
import { useFeatureFlags, flags } from 'lib/feature-flags'
import ScheduleCalendar from './calendar/schedule-calendar'
import AcademyScheduleView from './academy/academy-schedule-view'
import ScheduleSegmentControl from './schedule-segment-control'

const SCHEDULE_TAB_MY = 'my'
const SCHEDULE_TAB_ACADEMY = 'academy'

const SCHEDULE_SCOPE_OPTIONS = [
  { value: SCHEDULE_TAB_MY, label: 'My Schedule' },
  { value: SCHEDULE_TAB_ACADEMY, label: 'Academy' },
]

const useStyles = makeStyles(theme => ({
  containerStyles: {
    boxSizing: 'border-box',
    padding: '0 0 1rem',
    maxWidth: '100%',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '60px',
    },
  },
  scopeToggle: {
    marginBottom: theme.spacing(2),
  },
}))

const BookingsTabs = ({ onboardingSchedule, onMetadataChange }) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { user } = useAuth()
  const [
    isAcademyScheduleUiEnabled,
    academyScheduleAcademyId,
  ] = useFeatureFlags([
    flags.FLAG_ACADEMY_SCHEDULE_UI,
    flags.FLAG_ACADEMY_SCHEDULE_UI_ACADEMY_ID,
  ])
  const showAcademyToggle = isAcademyScheduleUiEnabled && user?.isAcademyOwner
  const academyScheduleId =
    academyScheduleAcademyId || user?.ownedAcademyIds?.[0]
  const [scheduleTab, setScheduleTab] = useState(SCHEDULE_TAB_MY)
  const viewingAcademySchedule =
    showAcademyToggle && scheduleTab === SCHEDULE_TAB_ACADEMY

  const isOnboardingComplete =
    onboardingSchedule.isGroupEventCreated ||
    onboardingSchedule.isBookingTypeCreated

  useEffect(() => {
    if (!viewingAcademySchedule) return
    onMetadataChange?.({ rangeLabel: '' })
  }, [viewingAcademySchedule, onMetadataChange])

  const scheduleScopeToggle = showAcademyToggle ? (
    <Box className={classes.scopeToggle}>
      <ScheduleSegmentControl
        value={scheduleTab}
        onChange={(_, next) => next && setScheduleTab(next)}
        options={SCHEDULE_SCOPE_OPTIONS}
        ariaLabel="Schedule scope"
        isMobile={isMobile}
      />
    </Box>
  ) : null

  return (
    <Container className={classes.containerStyles}>
      {!isOnboardingComplete && (
        <Box mt={3}>
          <Disclaimer
            logo={PGAIcon}
            description="For further assistance with your PGA Schedule setup, revisit the PGA schedule page to track your progress and access additional information."
            action={
              <Button
                variant={isMobile ? 'text' : 'outlined'}
                href={'/pga-coach/set-up-schedule/'}
              >
                Schedule set up guide
              </Button>
            }
          />
        </Box>
      )}
      {scheduleScopeToggle}
      {viewingAcademySchedule ? (
        <AcademyScheduleView academyId={academyScheduleId} />
      ) : (
        <ScheduleCalendar
          onMetadataChange={onMetadataChange}
          canBookStudent={isOnboardingComplete}
          bookingUrl={user?.coach?.bookingUrl}
        />
      )}
    </Container>
  )
}

export default BookingsTabs
